// Advanced Color System with Accessibility Compliance
// Based on WCAG 2.1 guidelines for AA and AAA compliance

import { AdvancedThemeConfig } from '@dynamic-flow/types';

export interface ColorVariation {
  name: string;
  percentage: number; // Darker (-) or Lighter (+) percentage from base
  hex: string;
  rgba: [number, number, number, number];
  contrastRatio?: number;
  accessibilityLevel?: 'AA' | 'AAA' | 'Fail';
}

export interface SemanticColor {
  name: string;
  purpose: string;
  baseColor: ColorVariation;
  variations: {
    120: ColorVariation; // 20% darker
    100: ColorVariation; // base color (0%)
    60: ColorVariation; // 40% lighter
    20?: ColorVariation; // 80% lighter (if applicable)
  };
  mostUsed: '120' | '100' | '60' | '20';
  accessibilityNotes?: string;
}

// Color manipulation utilities
export class ColorUtils {
  static hexToRgba(
    hex: string,
    alpha: number = 1
  ): [number, number, number, number] {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) throw new Error('Invalid hex color');

    return [
      parseInt(result[1], 16),
      parseInt(result[2], 16),
      parseInt(result[3], 16),
      alpha,
    ];
  }

  static rgbaToHex(r: number, g: number, b: number): string {
    return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
  }

  static adjustBrightness(hex: string, percentage: number): string {
    const [r, g, b] = this.hexToRgba(hex);

    const adjust = (color: number) => {
      if (percentage > 0) {
        // Lighter: move towards white
        return Math.round(color + (255 - color) * (percentage / 100));
      } else {
        // Darker: move towards black
        return Math.round(color * (1 + percentage / 100));
      }
    };

    const newR = Math.max(0, Math.min(255, adjust(r)));
    const newG = Math.max(0, Math.min(255, adjust(g)));
    const newB = Math.max(0, Math.min(255, adjust(b)));

    return this.rgbaToHex(newR, newG, newB);
  }

  static getContrastRatio(color1: string, color2: string): number {
    const [r1, g1, b1] = this.hexToRgba(color1);
    const [r2, g2, b2] = this.hexToRgba(color2);

    const getLuminance = (r: number, g: number, b: number) => {
      const [rs, gs, bs] = [r, g, b].map((c) => {
        c = c / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
      });
      return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
    };

    const lum1 = getLuminance(r1, g1, b1);
    const lum2 = getLuminance(r2, g2, b2);
    const brightest = Math.max(lum1, lum2);
    const darkest = Math.min(lum1, lum2);

    return (brightest + 0.05) / (darkest + 0.05);
  }

  static getAccessibilityLevel(contrastRatio: number): 'AA' | 'AAA' | 'Fail' {
    if (contrastRatio >= 7) return 'AAA';
    if (contrastRatio >= 4.5) return 'AA';
    return 'Fail';
  }

  static generateColorVariations(
    baseHex: string,
    colorType: string
  ): {
    120: ColorVariation;
    100: ColorVariation;
    60: ColorVariation;
    20: ColorVariation;
  } {
    const baseRgba = this.hexToRgba(baseHex);
    const baseContrast = this.getContrastRatio(baseHex, '#FFFFFF');

    return {
      120: {
        name: `${colorType}-120`,
        percentage: -20,
        hex: this.adjustBrightness(baseHex, -20),
        rgba: this.hexToRgba(this.adjustBrightness(baseHex, -20)) as [
          number,
          number,
          number,
          number,
        ],
        contrastRatio: this.getContrastRatio(
          this.adjustBrightness(baseHex, -20),
          '#FFFFFF'
        ),
        accessibilityLevel: this.getAccessibilityLevel(
          this.getContrastRatio(this.adjustBrightness(baseHex, -20), '#FFFFFF')
        ),
      },
      100: {
        name: `${colorType}-100`,
        percentage: 0,
        hex: baseHex,
        rgba: baseRgba as [number, number, number, number],
        contrastRatio: baseContrast,
        accessibilityLevel: this.getAccessibilityLevel(baseContrast),
      },
      60: {
        name: `${colorType}-60`,
        percentage: 40,
        hex: this.adjustBrightness(baseHex, 40),
        rgba: this.hexToRgba(this.adjustBrightness(baseHex, 40)) as [
          number,
          number,
          number,
          number,
        ],
        contrastRatio: this.getContrastRatio(
          this.adjustBrightness(baseHex, 40),
          '#FFFFFF'
        ),
        accessibilityLevel: this.getAccessibilityLevel(
          this.getContrastRatio(this.adjustBrightness(baseHex, 40), '#FFFFFF')
        ),
      },
      20: {
        name: `${colorType}-20`,
        percentage: 80,
        hex: this.adjustBrightness(baseHex, 80),
        rgba: this.hexToRgba(this.adjustBrightness(baseHex, 80)) as [
          number,
          number,
          number,
          number,
        ],
        contrastRatio: this.getContrastRatio(
          this.adjustBrightness(baseHex, 80),
          '#FFFFFF'
        ),
        accessibilityLevel: this.getAccessibilityLevel(
          this.getContrastRatio(this.adjustBrightness(baseHex, 80), '#FFFFFF')
        ),
      },
    };
  }
}

// Pre-defined semantic color configurations
export const semanticColors = {
  primary: {
    name: 'primary',
    purpose: 'Main brand color for primary actions and highlights',
    baseColor: {
      name: 'primary-100',
      percentage: 0,
      hex: '#3B82F6',
      rgba: [59, 130, 246, 1] as [number, number, number, number],
      contrastRatio: 4.5,
      accessibilityLevel: 'AA' as const,
    },
    variations: {
      120: {
        name: 'primary-120',
        percentage: -20,
        hex: '#1D4ED8',
        rgba: [29, 78, 216, 1] as [number, number, number, number],
        contrastRatio: 7.0,
        accessibilityLevel: 'AAA' as const,
      },
      100: {
        name: 'primary-100',
        percentage: 0,
        hex: '#3B82F6',
        rgba: [59, 130, 246, 1] as [number, number, number, number],
        contrastRatio: 4.5,
        accessibilityLevel: 'AA' as const,
      },
      60: {
        name: 'primary-60',
        percentage: 40,
        hex: '#93C5FD',
        rgba: [147, 197, 253, 1] as [number, number, number, number],
        contrastRatio: 3.0,
        accessibilityLevel: 'Fail' as const,
      },
      20: {
        name: 'primary-20',
        percentage: 80,
        hex: '#EFF6FF',
        rgba: [239, 246, 255, 1] as [number, number, number, number],
        contrastRatio: 1.2,
        accessibilityLevel: 'Fail' as const,
      },
    },
    mostUsed: '100' as const,
    accessibilityNotes:
      'Use 100 for primary buttons and links. 120 for hover states. 60 and 20 for backgrounds.',
  },
  warning: {
    name: 'warning',
    purpose: 'Alert users about potential issues or important information',
    baseColor: {
      name: 'warning-100',
      percentage: 0,
      hex: '#F59E0B',
      rgba: [245, 158, 11, 1] as [number, number, number, number],
      contrastRatio: 4.5,
      accessibilityLevel: 'AA' as const,
    },
    variations: {
      120: {
        name: 'warning-120',
        percentage: -20,
        hex: '#D97706',
        rgba: [217, 119, 6, 1] as [number, number, number, number],
        contrastRatio: 7.0,
        accessibilityLevel: 'AAA' as const,
      },
      100: {
        name: 'warning-100',
        percentage: 0,
        hex: '#F59E0B',
        rgba: [245, 158, 11, 1] as [number, number, number, number],
        contrastRatio: 4.5,
        accessibilityLevel: 'AA' as const,
      },
      60: {
        name: 'warning-60',
        percentage: 40,
        hex: '#FCD34D',
        rgba: [252, 211, 77, 1] as [number, number, number, number],
        contrastRatio: 3.0,
        accessibilityLevel: 'Fail' as const,
      },
      20: {
        name: 'warning-20',
        percentage: 80,
        hex: '#FFFBEB',
        rgba: [255, 251, 235, 1] as [number, number, number, number],
        contrastRatio: 1.2,
        accessibilityLevel: 'Fail' as const,
      },
    },
    mostUsed: '100' as const,
    accessibilityNotes:
      'Use 100 for warning messages and alerts. 120 for critical warnings. 60 and 20 for backgrounds.',
  },
  negative: {
    name: 'negative',
    purpose: 'Indicate errors, destructive actions, or critical issues',
    baseColor: {
      name: 'negative-100',
      percentage: 0,
      hex: '#EF4444',
      rgba: [239, 68, 68, 1] as [number, number, number, number],
      contrastRatio: 4.5,
      accessibilityLevel: 'AA' as const,
    },
    variations: {
      120: {
        name: 'negative-120',
        percentage: -20,
        hex: '#DC2626',
        rgba: [220, 38, 38, 1] as [number, number, number, number],
        contrastRatio: 7.0,
        accessibilityLevel: 'AAA' as const,
      },
      100: {
        name: 'negative-100',
        percentage: 0,
        hex: '#EF4444',
        rgba: [239, 68, 68, 1] as [number, number, number, number],
        contrastRatio: 4.5,
        accessibilityLevel: 'AA' as const,
      },
      60: {
        name: 'negative-60',
        percentage: 40,
        hex: '#FCA5A5',
        rgba: [252, 165, 165, 1] as [number, number, number, number],
        contrastRatio: 3.0,
        accessibilityLevel: 'Fail' as const,
      },
      20: {
        name: 'negative-20',
        percentage: 80,
        hex: '#FEF2F2',
        rgba: [254, 242, 242, 1] as [number, number, number, number],
        contrastRatio: 1.2,
        accessibilityLevel: 'Fail' as const,
      },
    },
    mostUsed: '100' as const,
    accessibilityNotes:
      'Use 100 for error messages and delete buttons. 120 for critical errors. 60 and 20 for backgrounds.',
  },
  information: {
    name: 'information',
    purpose: 'Provide informational content and secondary actions',
    baseColor: {
      name: 'information-100',
      percentage: 0,
      hex: '#06B6D4',
      rgba: [6, 182, 212, 1] as [number, number, number, number],
      contrastRatio: 4.5,
      accessibilityLevel: 'AA' as const,
    },
    variations: {
      120: {
        name: 'information-120',
        percentage: -20,
        hex: '#0891B2',
        rgba: [8, 145, 178, 1] as [number, number, number, number],
        contrastRatio: 7.0,
        accessibilityLevel: 'AAA' as const,
      },
      100: {
        name: 'information-100',
        percentage: 0,
        hex: '#06B6D4',
        rgba: [6, 182, 212, 1] as [number, number, number, number],
        contrastRatio: 4.5,
        accessibilityLevel: 'AA' as const,
      },
      60: {
        name: 'information-60',
        percentage: 40,
        hex: '#67E8F9',
        rgba: [103, 232, 249, 1] as [number, number, number, number],
        contrastRatio: 3.0,
        accessibilityLevel: 'Fail' as const,
      },
      20: {
        name: 'information-20',
        percentage: 80,
        hex: '#ECFEFF',
        rgba: [236, 254, 255, 1] as [number, number, number, number],
        contrastRatio: 1.2,
        accessibilityLevel: 'Fail' as const,
      },
    },
    mostUsed: '100' as const,
    accessibilityNotes:
      'Use 100 for info messages and secondary buttons. 120 for important info. 60 and 20 for backgrounds.',
  },
  success: {
    name: 'success',
    purpose: 'Indicate successful actions, confirmations, and positive states',
    baseColor: {
      name: 'success-100',
      percentage: 0,
      hex: '#10B981',
      rgba: [16, 185, 129, 1] as [number, number, number, number],
      contrastRatio: 4.5,
      accessibilityLevel: 'AA' as const,
    },
    variations: {
      120: {
        name: 'success-120',
        percentage: -20,
        hex: '#059669',
        rgba: [5, 150, 105, 1] as [number, number, number, number],
        contrastRatio: 7.0,
        accessibilityLevel: 'AAA' as const,
      },
      100: {
        name: 'success-100',
        percentage: 0,
        hex: '#10B981',
        rgba: [16, 185, 129, 1] as [number, number, number, number],
        contrastRatio: 4.5,
        accessibilityLevel: 'AA' as const,
      },
      60: {
        name: 'success-60',
        percentage: 40,
        hex: '#6EE7B7',
        rgba: [110, 231, 183, 1] as [number, number, number, number],
        contrastRatio: 3.0,
        accessibilityLevel: 'Fail' as const,
      },
      20: {
        name: 'success-20',
        percentage: 80,
        hex: '#ECFDF5',
        rgba: [236, 253, 245, 1] as [number, number, number, number],
        contrastRatio: 1.2,
        accessibilityLevel: 'Fail' as const,
      },
    },
    mostUsed: '100' as const,
    accessibilityNotes:
      'Use 100 for success messages and confirmations. 120 and 60 for backgrounds or non-critical elements.',
  },
};

// Neutral colors for backgrounds and text
export const neutralColors = {
  name: 'neutral',
  purpose: 'Background, text, and border colors',
  baseColor: {
    name: 'neutral-100',
    percentage: 0,
    hex: '#6B7280',
    rgba: [107, 114, 128, 1] as [number, number, number, number],
    contrastRatio: 4.5,
    accessibilityLevel: 'AA' as const,
  },
  variations: {
    120: {
      name: 'neutral-120',
      percentage: -20,
      hex: '#4B5563',
      rgba: [75, 85, 99, 1] as [number, number, number, number],
      contrastRatio: 7.0,
      accessibilityLevel: 'AAA' as const,
    },
    100: {
      name: 'neutral-100',
      percentage: 0,
      hex: '#6B7280',
      rgba: [107, 114, 128, 1] as [number, number, number, number],
      contrastRatio: 4.5,
      accessibilityLevel: 'AA' as const,
    },
    60: {
      name: 'neutral-60',
      percentage: 40,
      hex: '#9CA3AF',
      rgba: [156, 163, 175, 1] as [number, number, number, number],
      contrastRatio: 3.0,
      accessibilityLevel: 'Fail' as const,
    },
    20: {
      name: 'neutral-20',
      percentage: 80,
      hex: '#F3F4F6',
      rgba: [243, 244, 246, 1] as [number, number, number, number],
      contrastRatio: 1.2,
      accessibilityLevel: 'Fail' as const,
    },
  },
  mostUsed: '100' as const,
  accessibilityNotes:
    'Neutral colors should maintain sufficient contrast for text readability',
};

// Default semantic color palette
export const defaultSemanticColors = {
  warning: '#F59E0B',
  negative: '#EF4444',
  information: '#3A9FE8',
  success: '#10B981',
  secondary: '#008270', // Default secondary color
} as const;

// Interface for creating advanced themes
export interface CreateAdvancedThemeOptions {
  id: string;
  name: string;
  description?: string;
  colors: {
    primary: string;
    secondary?: string;
    warning?: string;
    negative?: string;
    information?: string;
    success?: string;
  };
  typography?: {
    fontFamily?: string;
  };
}

// Function to create advanced theme from base colors
export function createAdvancedTheme(
  options: CreateAdvancedThemeOptions
): AdvancedThemeConfig {
  const {
    id,
    name,
    description = `Advanced theme with ${name} color palette`,
    colors: {
      primary: primaryHex,
      secondary: secondaryHex = defaultSemanticColors.secondary,
      warning: warningHex = defaultSemanticColors.warning,
      negative: negativeHex = defaultSemanticColors.negative,
      information: informationHex = defaultSemanticColors.information,
      success: successHex = defaultSemanticColors.success,
    },
    typography: { fontFamily = 'Inter, system-ui, sans-serif' } = {},
  } = options;
  return {
    id,
    name,
    description,
    colors: {
      primary: {
        ...semanticColors.primary,
        variations: ColorUtils.generateColorVariations(primaryHex, 'primary'),
        baseColor: {
          name: 'primary-100',
          percentage: 0,
          hex: primaryHex,
          rgba: ColorUtils.hexToRgba(primaryHex) as [
            number,
            number,
            number,
            number,
          ],
          contrastRatio: ColorUtils.getContrastRatio(primaryHex, '#FFFFFF'),
          accessibilityLevel: ColorUtils.getAccessibilityLevel(
            ColorUtils.getContrastRatio(primaryHex, '#FFFFFF')
          ),
        },
      },
      secondary: secondaryHex
        ? {
            ...semanticColors.information, // Reuse information structure for secondary
            name: 'secondary',
            purpose: 'Secondary brand color for accents and highlights',
            variations: ColorUtils.generateColorVariations(
              secondaryHex,
              'secondary'
            ),
            baseColor: {
              name: 'secondary-100',
              percentage: 0,
              hex: secondaryHex,
              rgba: ColorUtils.hexToRgba(secondaryHex) as [
                number,
                number,
                number,
                number,
              ],
              contrastRatio: ColorUtils.getContrastRatio(
                secondaryHex,
                '#FFFFFF'
              ),
              accessibilityLevel: ColorUtils.getAccessibilityLevel(
                ColorUtils.getContrastRatio(secondaryHex, '#FFFFFF')
              ),
            },
          }
        : semanticColors.information,
      warning: warningHex
        ? {
            ...semanticColors.warning,
            variations: ColorUtils.generateColorVariations(
              warningHex,
              'warning'
            ),
            baseColor: {
              name: 'warning-100',
              percentage: 0,
              hex: warningHex,
              rgba: ColorUtils.hexToRgba(warningHex) as [
                number,
                number,
                number,
                number,
              ],
              contrastRatio: ColorUtils.getContrastRatio(warningHex, '#FFFFFF'),
              accessibilityLevel: ColorUtils.getAccessibilityLevel(
                ColorUtils.getContrastRatio(warningHex, '#FFFFFF')
              ),
            },
          }
        : semanticColors.warning,
      negative: negativeHex
        ? {
            ...semanticColors.negative,
            variations: ColorUtils.generateColorVariations(
              negativeHex,
              'negative'
            ),
            baseColor: {
              name: 'negative-100',
              percentage: 0,
              hex: negativeHex,
              rgba: ColorUtils.hexToRgba(negativeHex) as [
                number,
                number,
                number,
                number,
              ],
              contrastRatio: ColorUtils.getContrastRatio(
                negativeHex,
                '#FFFFFF'
              ),
              accessibilityLevel: ColorUtils.getAccessibilityLevel(
                ColorUtils.getContrastRatio(negativeHex, '#FFFFFF')
              ),
            },
          }
        : semanticColors.negative,
      information: informationHex
        ? {
            ...semanticColors.information,
            variations: ColorUtils.generateColorVariations(
              informationHex,
              'information'
            ),
            baseColor: {
              name: 'information-100',
              percentage: 0,
              hex: informationHex,
              rgba: ColorUtils.hexToRgba(informationHex) as [
                number,
                number,
                number,
                number,
              ],
              contrastRatio: ColorUtils.getContrastRatio(
                informationHex,
                '#FFFFFF'
              ),
              accessibilityLevel: ColorUtils.getAccessibilityLevel(
                ColorUtils.getContrastRatio(informationHex, '#FFFFFF')
              ),
            },
          }
        : semanticColors.information,
      success: successHex
        ? {
            ...semanticColors.success,
            variations: ColorUtils.generateColorVariations(
              successHex,
              'success'
            ),
            baseColor: {
              name: 'success-100',
              percentage: 0,
              hex: successHex,
              rgba: ColorUtils.hexToRgba(successHex) as [
                number,
                number,
                number,
                number,
              ],
              contrastRatio: ColorUtils.getContrastRatio(successHex, '#FFFFFF'),
              accessibilityLevel: ColorUtils.getAccessibilityLevel(
                ColorUtils.getContrastRatio(successHex, '#FFFFFF')
              ),
            },
          }
        : semanticColors.success,
      neutral: neutralColors,
    },
    typography: {
      fontFamily: fontFamily || 'Inter, system-ui, sans-serif',
    },
    accessibility: {
      aaCompliance: ColorUtils.getContrastRatio(primaryHex, '#FFFFFF') >= 4.5,
      aaaCompliance: ColorUtils.getContrastRatio(primaryHex, '#FFFFFF') >= 7,
      minContrastRatio: ColorUtils.getContrastRatio(primaryHex, '#FFFFFF'),
    },
  };
}

// Advanced CSS Variable generator for semantic color system
export function generateAdvancedCSSVariables(
  theme: AdvancedThemeConfig
): string {
  const c = theme.colors ?? {};

  const safe = (path: any, fallback: string = '#000000') => path ?? fallback;

  return `
    :root {
      --color-primary-120: ${safe(c.primary?.variations?.[120]?.hex)};
      --color-primary-100: ${safe(c.primary?.variations?.[100]?.hex)};
      --color-primary-60: ${safe(c.primary?.variations?.[60]?.hex)};
      --color-primary-20: ${safe(c.primary?.variations?.[20]?.hex, c.primary?.variations?.[60]?.hex)};

      --color-secondary-120: ${safe(c.secondary?.variations?.[120]?.hex)};
      --color-secondary-100: ${safe(c.secondary?.variations?.[100]?.hex)};
      --color-secondary-60: ${safe(c.secondary?.variations?.[60]?.hex)};
      --color-secondary-20: ${safe(c.secondary?.variations?.[20]?.hex, c.secondary?.variations?.[60]?.hex)};

      --color-warning-120: ${safe(c.warning?.variations?.[120]?.hex)};
      --color-warning-100: ${safe(c.warning?.variations?.[100]?.hex)};
      --color-warning-60: ${safe(c.warning?.variations?.[60]?.hex)};

      --color-negative-120: ${safe(c.negative?.variations?.[120]?.hex)};
      --color-negative-100: ${safe(c.negative?.variations?.[100]?.hex)};
      --color-negative-60: ${safe(c.negative?.variations?.[60]?.hex)};

      --color-information-120: ${safe(c.information?.variations?.[120]?.hex)};
      --color-information-100: ${safe(c.information?.variations?.[100]?.hex)};
      --color-information-60: ${safe(c.information?.variations?.[60]?.hex)};

      --color-success-120: ${safe(c.success?.variations?.[120]?.hex)};
      --color-success-100: ${safe(c.success?.variations?.[100]?.hex)};
      --color-success-60: ${safe(c.success?.variations?.[60]?.hex)};

      --color-neutral-120: ${safe(c.neutral?.variations?.[120]?.hex)};
      --color-neutral-100: ${safe(c.neutral?.variations?.[100]?.hex)};
      --color-neutral-60: ${safe(c.neutral?.variations?.[60]?.hex)};
      --color-neutral-20: ${safe(c.neutral?.variations?.[20]?.hex, c.neutral?.variations?.[60]?.hex)};

      --color-white: #FFFFFF;
      --color-black: #000000;
    }
  `;
}

export const ADVANCED_THEME_IDS = {
  CUSTOM: 'custom-advanced',
  DEFAULT: 'default-advanced',
} as const;

export const ADVANCED_THEME_LABELS = {
  [ADVANCED_THEME_IDS.CUSTOM]: {
    name: 'Custom Theme',
    description: 'Customized advanced color palette',
  },
  [ADVANCED_THEME_IDS.DEFAULT]: {
    name: 'System Default',
    description: 'System default color palette',
  },
} as const;

export function resolveBrandThemeFromAdvanced(
  advancedThemeId?: string
): 'bh' | 'vtl' | null {
  if (
    !advancedThemeId ||
    advancedThemeId === ADVANCED_THEME_IDS.CUSTOM ||
    advancedThemeId === ADVANCED_THEME_IDS.DEFAULT
  ) {
    return null;
  }

  if (advancedThemeId === 'bh-preset') {
    return 'bh';
  }

  if (advancedThemeId === 'vtl-preset') {
    return 'vtl';
  }

  return null;
}

// Helper function to create theme presets with default semantic colors
export function createThemePreset(
  id: string,
  name: string,
  primaryColor: string,
  secondaryColor?: string,
  fontFamily?: string
): AdvancedThemeConfig {
  return createAdvancedTheme({
    id,
    name,
    description: `Preset theme: ${name}`,
    colors: {
      primary: primaryColor,
      secondary: secondaryColor,
      // All other colors use defaults from defaultSemanticColors
    },
    typography: {
      fontFamily,
    },
  });
}
